import { BrowserHistoryNavigation } from 'aeppic'
import { parse as queryParse, stringify as queryStringify } from 'query-string'

import { checkRoutes } from './routing/index.js'

import { getCastingShare, getModelShare } from './route-actions/index.js'

const SUBPATH_PARAMS = new Set(['aspect'])

function extractSubpathParams(url) {
  const params = {}
  const pathPart = url.split(/[?#]/)[0]
  for (const name of SUBPATH_PARAMS) {
    const regex = new RegExp(`/${name}/([^/?#]+)`)
    const match = pathPart.match(regex)
    if (match) {
      params[name] = decodeURIComponent(match[1])
    }
  }
  return params
}

function stripSubpathParams(url) {
  let result = url
  for (const name of SUBPATH_PARAMS) {
    result = result.replace(new RegExp(`/${name}/[^/?#]+`), '')
  }
  return result
}

function buildSubpathSegments(params) {
  const segments = []
  for (const [name, value] of Object.entries(params)) {
    if (SUBPATH_PARAMS.has(name) && value != null && value !== false && value !== '') {
      segments.push(`/${name}/${encodeURIComponent(String(value))}`)
    }
  }
  return segments.join('')
}

function extractDocumentId(url) {
  let path = url
  // Strip protocol and host if present
  if (path.includes('://')) {
    try { path = new URL(path).pathname } catch (e) { path = path.slice(path.indexOf('/', path.indexOf('://') + 3)) }
  }
  path = stripSubpathParams(path)
  path = path.split(/[?#]/)[0]
  return path.replace(/^\/+/, '').replace(/\/+$/, '')
}

// Expose a factory function that creates a fresh set of store, router,
// app instances on each call (which is called for each SSR request)
export function createApp({ Aeppic, uuid }, options = {}) {
  let fetchToUse = options.fetch

  if (!fetchToUse && typeof fetch !== 'undefined') {
    fetchToUse = fetch
  }

  /* eslint-disable */
  const aeppic = new Aeppic({
    el: options.el,
    auth: options.auth,
    fetch: fetchToUse,
    persistenceAdapter: options.persistenceAdapter,
    localReadModel: options.localReadModel,
    localReadModelFactory: options.localReadModelFactory,
    server: options.server,
    serverQueryBatchSize: options.serverQueryBatchSize,
    serverQueryTimeoutMs: options.serverQueryTimeoutMs,
    boot: options.boot,
    load: options.load,
    flags: options.flags,
    removeAfterBoot: options.removeAfterBoot,
    removeAfterLoad: options.removeAfterLoad,
    removeAfterRender: options.removeAfterRender,
    booted: options.booted,
    loaded: options.loaded || onloaded,
    searchAllFields: options.searchAllFields,
    vueComponentOptions: options.vueComponentOptions,
    Logger: options.Logger,
  })

  async function onloaded() {
    // console.log('onloaded')
    const checkRoot = await aeppic.get('root')

    if (!checkRoot) {
      console.error('Cannot find `root`', aeppic.Model.numberOfDocuments);
    }

    let url = options.url
    // Normalize to path only
    if (url.includes('://')) {
      try { url = new URL(url).pathname + new URL(url).search + new URL(url).hash } catch (e) {}
    }
    const urlWithoutSubpaths = stripSubpathParams(url)
    const initialSubpaths = extractSubpathParams(url)

    let initialDocumentId = ''
    let layoutId = null //'5913cb1e-9979-4eb7-84e8-488cdd658cbb'

    const match = await checkRoutes(aeppic, urlWithoutSubpaths)

    if (match) {
      let share = null

      if (match.route.id === '3cc5f99e-0b63-4241-b963-6aac470e050a' || match.route.id === '7d831ebf-151b-47ea-9e83-ebdc18b044dc') {
        share = await getCastingShare(aeppic, match)
      } else if (match.route.id === 'a17ed61d-4f83-43cd-81c4-f0d4a0082eac' || match.route.id === '3b0b1a68-5772-4e45-8b10-c3c8b3d0c48e') {
        share = await getModelShare(aeppic, match)
      } else if (match.route.id === '6f88557a-2386-4d3f-ae03-39d9df464960') {
        share = (await aeppic.find(`f.id:804bed89-803a-4090-8b1b-857cab8ec6dd data.identifier:${match.params.sharedMailId}`))[0]
      }

      if (share) {
        if (share.data.targetInfo) {
          initialDocumentId = share.data.targetInfo
        }

        if (match.route.data.layout?.id && !match.route.data.isDynamic) {
          layoutId = match.route.data.layout.id
        }
      } else {
        if (match.route.data.layout?.id && !match.route.data.isDynamic) {
          layoutId = match.route.data.layout.id
        }

        if (match.params.pageDocumentId) {
          initialDocumentId = match.params.pageDocumentId
        }
      }
    }

    if (!layoutId) {
      const BIZPIN_LAYOUT_ID = '5913cb1e-9979-4eb7-84e8-488cdd658cbb'
      layoutId = aeppic.Features.getOptionsFor('default-layout')?.layoutId ?? BIZPIN_LAYOUT_ID

      initialDocumentId = extractDocumentId(url)
    }

    startRouting(aeppic, layoutId, initialDocumentId, initialSubpaths)
  }

  return { app: aeppic._vue, aeppic: aeppic, uuid }
}

async function startRouting(aeppic, layoutId, defaultStartDocumentId, initialSubpaths = {}) {
  let lastDocumentId = null

  function applyRouteParams(queryParams, subpathParams) {
    const nextParams = { ...aeppic.Root.params, queryParams }
    for (const name of SUBPATH_PARAMS) {
      delete nextParams[name]
    }
    Object.assign(nextParams, subpathParams)
    aeppic.Root.params = nextParams
    aeppic.dispatchDOMEvent('ae-route-params-changed', {
      ...queryParams,
      ...subpathParams,
      routeParams: { query: queryParams, subpath: subpathParams },
    })
  }
  const browserHistory = new BrowserHistoryNavigation(window,
    detail => aeppic.dispatchDOMEvent('ae-browser-history-navigation', detail),
    () => aeppic.confirmNavigation())

  aeppic.Router = {
    reserveBrowserBackEntry() {
      browserHistory.reserveBackEntry()
    },
    registerSubpathParam(name) {
      SUBPATH_PARAMS.add(name)
    },

    async onLinkClicked(event, href) {
      event?.preventDefault?.()
      const confirmation = aeppic.confirmNavigation()
      if (confirmation && !await confirmation) return
      const now = Date.now().toString()

      const pageDocumentId = extractDocumentId(href)
      const routeSubpathParams = extractSubpathParams(href)

      browserHistory.write({ href, timestamp: now }, href)

      const urlHash = queryParse(href).hash
      const queryParams = queryParse(href).search

      lastDocumentId = pageDocumentId

      renderPage(layoutId, pageDocumentId, {
        queryParams,
        urlHash,
        timestamp: now,
        ...routeSubpathParams
      })

      if (event) {
        event.preventDefault()
      }
    },
    async onNavigateTo(pageDocumentId, params, options = {}) {
      const confirmation = aeppic.confirmNavigation()
      if (confirmation && !await confirmation) return
      const now = Date.now().toString()

      const { forceNavigate, ...remainingParams } = params || {}

      const subpathValues = {}
      const queryParamValues = {}

      for (const [key, value] of Object.entries(remainingParams)) {
        if (SUBPATH_PARAMS.has(key)) {
          subpathValues[key] = value
        } else {
          queryParamValues[key] = value
        }
      }

      const subpathSegments = buildSubpathSegments(subpathValues)
      const queryString = queryStringify(queryParamValues)

      let url = pageDocumentId + subpathSegments
      if (queryString) {
        url += '?' + queryString
      }

      if (!forceNavigate && lastDocumentId === pageDocumentId) {
        if (options.replace) {
          browserHistory.write({ href: url, timestamp: now }, url, true)
        } else {
          browserHistory.write({ href: url, timestamp: now }, url)
        }
        applyRouteParams(queryParamValues, subpathValues)
        return
      }

      if (options.replace) {
        browserHistory.write({ href: url, queryParams: queryParamValues, timestamp: now }, url, true)
      } else {
        browserHistory.write({ href: url, queryParams: queryParamValues, timestamp: now }, url)
      }

      lastDocumentId = pageDocumentId

      renderPage(layoutId, pageDocumentId, {
        queryParams: queryParamValues,
        timestamp: now,
        ...subpathValues
      })
    },

    async updateRouteParams(params, options) {
      const confirmation = aeppic.confirmNavigation()
      if (confirmation && !await confirmation) return
      const { replace } = options || {}
      const now = Date.now().toString()
      const currentUrl = window.location.pathname + (window.location.search || '') + (window.location.hash || '')
      const baseUrl = stripSubpathParams(currentUrl)

      const parsedUrl = new URL(currentUrl, window.location.href)
      const existingQuery = getQueryParams(parsedUrl.search)
      const existingSubpaths = extractSubpathParams(currentUrl)
      const merged = { ...existingQuery, ...existingSubpaths, ...params }
      const queryValues = {}
      const subpathValues = {}
      for (const [key, value] of Object.entries(merged)) {
        if (value == null || value === false || value === '') {
          continue
        }
        if (SUBPATH_PARAMS.has(key)) {
          subpathValues[key] = value
        } else {
          queryValues[key] = value
        }
      }

      const query = queryStringify(queryValues)
      const path = baseUrl.split(/[?#]/)[0] + buildSubpathSegments(subpathValues)
      const newUrl = path + (query ? '?' + query : '') + parsedUrl.hash
      browserHistory.write({ href: newUrl, queryParams: queryValues, timestamp: now }, newUrl, replace)
      applyRouteParams(queryValues, subpathValues)
    }
  }

  const queryParams = getQueryParams(typeof window !== 'undefined' && window.location.search)
  const urlHash = getWindowLocationUrlHash()
  const now = Date.now().toString()

  if (typeof window !== 'undefined') {
    window.onpopstate = (e) => {
      if (browserHistory.intercept(e.state)) {
        return
      }
      if (e.state) {
        const href = e.state.href
        const newDocId = extractDocumentId(href)
        const timestamp = e.state.timestamp
        const routeSubpathParams = extractSubpathParams(href)
        const stateQueryParams = getQueryParams(new URL(href, window.location.href).search)

        if (newDocId === lastDocumentId) {
          // Same document — update params in place, no full re-render
          applyRouteParams(stateQueryParams, routeSubpathParams)
          return
        }

        lastDocumentId = newDocId

        renderPage(layoutId, newDocId, {
          queryParams: stateQueryParams,
          historic: true,
          timestamp,
          ...routeSubpathParams
        })
      }
    }

    browserHistory.write({ href: window.location.href, queryParams, urlHash, timestamp: now }, window.location.href, true)

    lastDocumentId = extractDocumentId(window.location.pathname)
  }

  if (queryParams.impersonationToken) {
    await aeppic.login(null, null, false, true, queryParams.impersonationToken)
  }

  renderPage(layoutId, defaultStartDocumentId, {
    queryParams,
    urlHash,
    timestamp: now,
    ...initialSubpaths
  })

  async function renderPage(layoutId, pageDocumentId, options = {}) {
    aeppic.renderLayout(layoutId, {
      params: {
        pageDocumentId,
        queryParams: options.queryParams,
        urlHash: options.urlHash,
        history: {
          historic: options.historic || false,
          timestamp: options.timestamp
        },
        aspect: options.aspect,
      }
    })
  }
}

function getQueryParams(fromString) {
  if (!fromString) {
    return {}
  }

  const paramsAsRegularObject = Object.assign({}, queryParse(fromString))

  return paramsAsRegularObject
}

function getWindowLocationUrlHash() {
  if (typeof window === 'undefined') {
    return null
  }

  return window.location.hash
}
